import { isProSlideWidget } from './campaignProWidgets';

export enum ProFollowUpInterval {
  MONTH_1 = 'MONTH_1',
  MONTH_3 = 'MONTH_3',
  MONTH_6 = 'MONTH_6',
  YEAR_1 = 'YEAR_1',
  YEAR_2 = 'YEAR_2',
  YEAR_3 = 'YEAR_3',
  YEAR_4 = 'YEAR_4',
  YEAR_5 = 'YEAR_5',
  YEAR_6 = 'YEAR_6',
  YEAR_7 = 'YEAR_7',
  YEAR_8 = 'YEAR_8',
  YEAR_9 = 'YEAR_9',
  YEAR_10 = 'YEAR_10',
}

export enum ProFollowUpAnchor {
  ENROLLMENT = 'ENROLLMENT',
  SURGERY = 'SURGERY',
}

export const PRO_FOLLOW_UP_INTERVALS: { value: ProFollowUpInterval; label: string }[] = [
  { value: ProFollowUpInterval.MONTH_1, label: '1 month' },
  { value: ProFollowUpInterval.MONTH_3, label: '3 months' },
  { value: ProFollowUpInterval.MONTH_6, label: '6 months' },
  { value: ProFollowUpInterval.YEAR_1, label: '1 year' },
  { value: ProFollowUpInterval.YEAR_2, label: '2 years' },
  { value: ProFollowUpInterval.YEAR_3, label: '3 years' },
  { value: ProFollowUpInterval.YEAR_4, label: '4 years' },
  { value: ProFollowUpInterval.YEAR_5, label: '5 years' },
  { value: ProFollowUpInterval.YEAR_6, label: '6 years' },
  { value: ProFollowUpInterval.YEAR_7, label: '7 years' },
  { value: ProFollowUpInterval.YEAR_8, label: '8 years' },
  { value: ProFollowUpInterval.YEAR_9, label: '9 years' },
  { value: ProFollowUpInterval.YEAR_10, label: '10 years' },
];

export const ALL_PRO_FOLLOW_UP_INTERVALS = PRO_FOLLOW_UP_INTERVALS.map((item) => item.value);

/** Pre-checked when follow-ups are turned on with nothing selected yet. Years 2–10 stay opt-in. */
export const DEFAULT_PRO_FOLLOW_UP_INTERVALS: ProFollowUpInterval[] = [
  ProFollowUpInterval.MONTH_1,
  ProFollowUpInterval.MONTH_3,
  ProFollowUpInterval.MONTH_6,
  ProFollowUpInterval.YEAR_1,
];

type FollowUpLeadWidget = {
  widgetId: string
  interval?: string | null
};

export function toLeadWidgetInput(widgets: { widgetId: string; label?: string; values?: string[]; content?: string }[] | undefined) {
  return (widgets ?? []).map((widget) => ({
    widgetId: widget.widgetId,
    label: widget.label,
    values: widget.values,
    content: widget.content,
  }));
}

export function getFollowUpTokenFromSearch(searchParams: URLSearchParams | null | undefined): string | undefined {
  const token = searchParams?.get('followUp')?.trim();
  return token || undefined;
}

export function formatFollowUpLabel(interval?: string | null): string {
  if (!interval) {
    return 'Baseline';
  }
  const match = PRO_FOLLOW_UP_INTERVALS.find((item) => item.value === interval);
  return match?.label ?? interval;
}

export function intervalsEqual(a?: string | null, b?: string | null): boolean {
  return (a ?? null) === (b ?? null);
}

export function findLeadWidgetByWidgetId<T extends FollowUpLeadWidget>(
  widgets: T[] | undefined,
  widgetId: string,
  interval?: string | null,
): T | undefined {
  const list = widgets ?? [];
  if (interval) {
    return list.find((widget) => widget.widgetId === widgetId && intervalsEqual(widget.interval, interval));
  }
  return list.find((widget) => widget.widgetId === widgetId && widget.interval == null)
    ?? list.find((widget) => widget.widgetId === widgetId);
}

export function collectProWidgetIdsFromPitch(pitch?: {
  slides?: {
    widgets?: { id?: string; widgetType?: string; label?: string | null; proInstrument?: string | null }[];
    widgetStructure?: { id?: string; type?: string; label?: string | null; proInstrument?: string | null }[];
  }[];
}): string[] {
  const ids: string[] = [];
  for (const slide of pitch?.slides ?? []) {
    for (const widget of slide.widgets ?? []) {
      if (widget.id && isProSlideWidget(widget)) {
        ids.push(widget.id);
      }
    }
    for (const structure of slide.widgetStructure ?? []) {
      if (structure.id && isProSlideWidget({
        widgetType: structure.type,
        label: structure.label,
        proInstrument: structure.proInstrument,
      })) {
        ids.push(structure.id);
      }
    }
  }
  return ids;
}

export function filterLeadWidgetsForFollowUp<T extends FollowUpLeadWidget>(
  widgets: T[] | undefined,
  options: {
    followUpToken?: string | null;
    followUpInterval?: string | null;
    followUpAlreadySubmitted?: boolean;
    proWidgetIds?: Iterable<string>;
  },
): T[] {
  const list = widgets ?? [];
  if (!options.followUpToken) {
    const byId = new Map<string, T>();
    for (const widget of list) {
      const existing = byId.get(widget.widgetId);
      if (!existing) {
        byId.set(widget.widgetId, widget);
        continue;
      }
      if (existing.interval != null && widget.interval == null) {
        byId.set(widget.widgetId, widget);
      }
    }
    return Array.from(byId.values());
  }

  if (options.followUpAlreadySubmitted) {
    return list.filter((widget) => intervalsEqual(widget.interval, options.followUpInterval));
  }

  const proIds = new Set(options.proWidgetIds ?? []);
  return list.filter((widget) => {
    if (proIds.has(widget.widgetId)) {
      return intervalsEqual(widget.interval, options.followUpInterval);
    }
    return widget.interval == null;
  });
}
